import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

interface NudgeHintProps {
  isVague: boolean;
  sessionDismissKey?: string;
}

const SESSION_KEY_PREFIX = "nudge_dismissed_";

export function NudgeHint({ isVague, sessionDismissKey = "default" }: NudgeHintProps) {
  const storageKey = SESSION_KEY_PREFIX + sessionDismissKey;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch { }
    setDismissed(true);
  };

  const visible = isVague && !dismissed;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="nudge-hint"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="flex items-start justify-between gap-2 mt-1.5 px-3 py-2 rounded-lg bg-primary/5 border border-primary/15"
          data-testid="nudge-hint"
        >
          <p className="text-xs text-muted-foreground leading-snug">
            <span className="text-primary/80 font-medium">Add a name, place, or detail</span>
            {" "}— it makes your post feel real.{" "}
            <span className="italic text-muted-foreground/70">e.g. "had a meeting with Sarah at our London office"</span>
          </p>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss hint"
            className="shrink-0 mt-0.5 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            data-testid="nudge-hint-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
